import React, { useEffect, useState } from "react";
import { Image, Text, TouchableOpacity, View, StyleSheet } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { resourceTileLabels } from "./chapterResourceTileData";

const PWO_PLACEHOLDER = require("./assets/pwo_scene_placeholder.png");
const VIDEO_PLACEHOLDER = require("./assets/video_segment_placeholder.png");

export default function ChapterResourceTile({ kind, asset, imageUri, description, selected, visitCount = 0, isDarkMode, onPress, t }) {
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => { setImageFailed(false); }, [imageUri]);
  const isPwo = kind === "pwo";
  const { title, subtitle } = resourceTileLabels(kind, asset);
  const translate = t || ((text) => text);
  const source = imageUri && !imageFailed
    ? { uri: imageUri }
    : (isPwo ? PWO_PLACEHOLDER : VIDEO_PLACEHOLDER);
  const foreground = isDarkMode ? "#e2e8f0" : "#0f172a";
  const muted = isDarkMode ? "#94a3b8" : "#64748b";
  const accent = isPwo ? "#059669" : "#4f46e5";

  return (
    <TouchableOpacity
      activeOpacity={0.88}
      accessibilityRole="button"
      accessibilityLabel={`${isPwo ? translate("Explore in 3D") : translate("Watch")} — ${title}`}
      accessibilityState={{ selected: !!selected }}
      onPress={onPress}
      style={[styles.card, {
        backgroundColor: isDarkMode ? "#192337" : "#fff",
        borderColor: selected ? accent : visitCount > 0 ? (isDarkMode ? "#047857" : "#a7f3d0") : (isDarkMode ? "#334155" : "#e2e8f0"),
        borderWidth: selected ? 2 : 1,
      }]}
    >
      <View style={styles.cover}>
        <Image
          source={source}
          resizeMode="cover"
          onError={() => setImageFailed(true)}
          accessibilityLabel={imageUri && !imageFailed ? title : (isPwo ? "3D exploration placeholder" : "Video placeholder")}
          style={styles.coverImage}
        />
        <View style={styles.mediaPill}>
          <MaterialCommunityIcons name={isPwo ? "cube-outline" : "play-circle-outline"} size={15} color="#fff" />
          <Text style={styles.mediaPillText}>{isPwo ? translate("EXPLORE IN 3D") : translate("WATCH VIDEO")}</Text>
        </View>
        {visitCount > 0 ? (
          <View style={styles.visitedPill}>
            <MaterialCommunityIcons name="check-circle" size={14} color="#047857" />
            <Text style={styles.visitedPillText}>{`${translate("Opened")} ${visitCount}×`}</Text>
          </View>
        ) : null}
        <View style={styles.playPill}>
          <MaterialCommunityIcons name={isPwo ? "cube-scan" : "play"} size={20} color="#fff" />
        </View>
      </View>
      <View style={styles.details}>
        <Text style={[styles.title, { color: foreground }]} numberOfLines={2}>{title}</Text>
        {subtitle ? <Text style={[styles.subtitle, { color: muted }]} numberOfLines={1}>{subtitle}</Text> : null}
        {description ? <Text style={[styles.description, { color: muted }]} numberOfLines={2}>{description}</Text> : null}
        <View style={styles.actionRow}>
          <Text style={[styles.action, { color: accent }]}>{selected ? translate("Close") : (isPwo ? translate("Open 3D") : translate("Play video"))}</Text>
          <MaterialCommunityIcons name={selected ? "chevron-up" : "arrow-right"} size={17} color={accent} />
        </View>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: { width: "100%", borderRadius: 17, overflow: "hidden" },
  cover: { aspectRatio: 16 / 9, width: "100%", backgroundColor: "#ecf1f8" },
  coverImage: { width: "100%", height: "100%" },
  mediaPill: { position: "absolute", left: 12, top: 12, borderRadius: 22, backgroundColor: "rgba(15,23,42,0.82)", paddingVertical: 6, paddingHorizontal: 9, flexDirection: "row", alignItems: "center", gap: 5 },
  mediaPillText: { fontSize: 10, fontWeight: "800", letterSpacing: 0.5, color: "#fff" },
  visitedPill: { position: "absolute", right: 12, top: 12, borderRadius: 20, backgroundColor: "#ecfdf5", paddingHorizontal: 9, paddingVertical: 6, flexDirection: "row", alignItems: "center", gap: 5 },
  visitedPillText: { fontSize: 11, color: "#047857", fontWeight: "800" },
  playPill: { position: "absolute", right: 12, bottom: 12, height: 40, width: 40, borderRadius: 20, backgroundColor: "rgba(15,23,42,0.78)", alignItems: "center", justifyContent: "center" },
  details: { paddingVertical: 13, paddingHorizontal: 15, minHeight: 114 },
  title: { fontWeight: "800", fontSize: 15, lineHeight: 21 },
  subtitle: { fontSize: 12, marginTop: 3 },
  description: { fontSize: 12, lineHeight: 17, marginTop: 6 },
  actionRow: { flexDirection: "row", alignItems: "center", gap: 4, marginTop: 11 },
  action: { fontSize: 12, fontWeight: "800" },
});
